import React, { useEffect, useState } from 'react';
import { DEFAULT_PLANOGRAM_COMPLIANCE_CONFIG } from '../../constants/planogramComplianceConfig';
import { useAuthContext } from '../../context/AuthContext';
import { useNotificationContext } from '../../context/NotificationContext';
import { useProductRecognition } from '../../hooks/useProductRecognition';
import { ApiService } from '../../services/api';
import { PlanogramComplianceResponse } from '../../types';
import { Audit } from './components/Audit';

interface ImageAuditPageProps {
  imageId: string | null;
}

export const ImageAuditPage: React.FC<ImageAuditPageProps> = ({ imageId }) => {
  const { token } = useAuthContext();
  const { showErrorToast } = useNotificationContext();

  // Use the product recognition hook
  const {
    productRecognitionData,
    setProductRecognitionData,
    isRunningModel,
    runProductRecognitionModel,
  } = useProductRecognition();

  // State for planogram compliance data
  const [planogramComplianceData, setPlanogramComplianceData] =
    useState<PlanogramComplianceResponse | null>(null);

  useEffect(() => {
    if (!imageId || !token) return;
    runProductRecognitionModel(Number(imageId), { useApiCache: true });
  }, [imageId, token, runProductRecognitionModel]);

  useEffect(() => {
    const fetchPlanogramCompliance = async () => {
      if (!token || !productRecognitionData || planogramComplianceData) {
        return;
      }
      try {
        const payload = {
          ...productRecognitionData,
          config: DEFAULT_PLANOGRAM_COMPLIANCE_CONFIG,
        };
        const data = await ApiService.createPlanogramComplianceFromPayload(token, payload, true);
        setPlanogramComplianceData(data);
      } catch (err) {
        showErrorToast(err instanceof Error ? err.message : 'Failed to fetch planogram compliance');
      }
    };
    fetchPlanogramCompliance();
  }, [token, productRecognitionData, planogramComplianceData, showErrorToast]);

  return (
    <Audit
      productRecognitionData={productRecognitionData}
      planogramComplianceData={planogramComplianceData}
      isLoading={isRunningModel}
      imageId={imageId}
      onProductRecognitionDataChange={setProductRecognitionData}
      onPlanogramComplianceDataChange={setPlanogramComplianceData}
    />
  );
};

export default ImageAuditPage;
